// ---------------------------------------------------------------------------
// Windy Webcam API v3
// Docs: https://api.windy.com/webcams
// Free tier: 500 req/day — sufficient for a 6h-cached global fetch
// ---------------------------------------------------------------------------

export interface WindyWebcam {
  webcamId: string;
  title: string;
  status: 'active' | 'inactive';
  location: {
    city: string;
    region: string;
    country: string;
    continent: string;
    latitude: number;
    longitude: number;
    timezone: string;
  };
  images: {
    current: {
      thumbnail: string;  // small preview JPG
      preview: string;    // larger preview JPG
    };
    daylight: boolean;
  };
  player: {
    day: {
      embed: string;  // iframe-embeddable URL (no YouTube restrictions)
      web: string;    // direct webcam page
    };
    night?: {
      embed: string;
      web: string;
    };
  };
  urls: {
    website: string;
  };
}

interface WindyListResponse {
  total: number;
  limit: number;
  offset: number;
  webcams: WindyWebcam[];
}

// Always route through /api/windy (Vercel serverless function) — avoids CORS in both dev and prod.
// Vite dev server proxies /api/windy → the same endpoint via vite.config.ts windyApiPlugin.
export const HAS_WINDY = true; // server-side proxy always available
const BASE = '/api/windy';
const CACHE_KEY = 'detectx_windy_webcams_v3';
const CACHE_TTL_MS = 6 * 60 * 60 * 1000; // 6 hours

async function fetchByContinent(continent: string, limit: number): Promise<WindyWebcam[]> {
  const params = new URLSearchParams({
    limit: String(limit),
    offset: '0',
    lang: 'en',
    include: 'location,player,images,urls',
    status: 'active',
    sortKey: 'popularity',
    sortDirection: 'desc',
    continents: continent,
  });
  const res = await fetch(`${BASE}?${params}`);
  if (!res.ok) throw new Error(`Windy API ${res.status} [${continent}]: ${res.statusText}`);
  const json = await res.json() as WindyListResponse;
  return json.webcams ?? [];
}

/** 특정 좌표 주변 웹캠. 대륙 인기순만으로는 서울 같은 특정 도시가 잡히지 않는다. */
async function fetchNearby(lat: number, lon: number, radiusKm: number, limit: number): Promise<WindyWebcam[]> {
  const params = new URLSearchParams({
    limit: String(limit),
    offset: '0',
    lang: 'en',
    include: 'location,player,images,urls',
    status: 'active',
    nearby: `${lat},${lon},${radiusKm}`,
  });
  const res = await fetch(`${BASE}?${params}`);
  if (!res.ok) throw new Error(`Windy nearby ${res.status}`);
  const json = await res.json() as WindyListResponse;
  return json.webcams ?? [];
}

/** 지정학 관심 도시 — 대륙 인기순에서 밀리므로 좌표로 직접 끌어온다. */
const STRATEGIC_CITIES: [string, number, number, number, number][] = [
  ['Seoul',     37.5665, 126.9780, 60, 12],
  ['Tokyo',     35.6762, 139.6503, 50, 8],
  ['Taipei',    25.0330, 121.5654, 50, 6],
  ['Singapore',  1.3521, 103.8198, 50, 6],
  ['Hong Kong', 22.3193, 114.1694, 40, 6],
];

// Continent code → limit  (total = 120 cams, 6 API requests per 6h refresh)
const CONTINENT_FETCH: [string, number][] = [
  ['EU', 30],  // Europe — highest density
  ['AS', 30],  // Asia
  ['NA', 20],  // North America
  ['SA', 15],  // South America
  ['AF', 15],  // Africa
  ['OC', 10],  // Oceania
];

/** Fetch top webcams per continent for global geographic diversity, with 6h localStorage cache. */
export async function fetchWindyWebcams(): Promise<WindyWebcam[]> {

  // Check cache first
  try {
    const raw = localStorage.getItem(CACHE_KEY);
    if (raw) {
      const { ts, data } = JSON.parse(raw) as { ts: number; data: WindyWebcam[] };
      if (Date.now() - ts < CACHE_TTL_MS && Array.isArray(data) && data.length > 0) {
        return data;
      }
    }
  } catch { /* ignore corrupt cache */ }

  // Fetch top webcams from each continent in parallel (6 requests total)
  const results = await Promise.allSettled([
    ...CONTINENT_FETCH.map(([code, limit]) => fetchByContinent(code, limit)),
    ...STRATEGIC_CITIES.map(([, lat, lon, r, limit]) => fetchNearby(lat, lon, r, limit)),
  ]);
  const raw2 = results.flatMap((r) => r.status === 'fulfilled' ? r.value : []);
  console.log('[Windy] raw webcams:', raw2.length);

  // API returns player.day as string URL, not {embed, web} object — normalize to expected shape
  const withEmbed = raw2.map((c) => {
    const id = String((c as unknown as { webcamId: string | number }).webcamId ?? c.webcamId);
    return {
      ...c,
      webcamId: id,
      player: {
        day: {
          embed: `https://webcams.windy.com/webcams/public/embed/player/${id}/day`,
          web: `https://www.windy.com/webcams/${id}`,
        },
      },
    };
  });

  const all = withEmbed.filter(
    (c) => c.status === 'active' && c.location?.latitude,
  );
  console.log('[Windy] after filter:', all.length, 'with embed:', all.filter(c => c.player?.day?.embed).length);

  try {
    localStorage.setItem(CACHE_KEY, JSON.stringify({ ts: Date.now(), data: all }));
  } catch { /* storage full, skip */ }

  return all;
}
